import getStatesList from '@/assets/js/shared/helpers/getStatesList';
import formatPlaceName from '@/assets/js/helpers/formatPlaceName';

const maxPlaceLength = 40;
const maxKeywordLength = 120;
const washingtonDc = { name: 'Washington, DC', state: 'DC' };
const washingtonDcNames = new Set([
  'dc',
  'washington dc',
  'district of columbia'
]);
const statesByCode = getStatesList();
const stateCodesByName = Object.keys(statesByCode).reduce((codes, code) => {
  codes[statesByCode[code].toLowerCase()] = code;
  return codes;
}, {});
// Longest first so `west virginia` matches before `virginia`
const stateNames = Object.keys(stateCodesByName).sort(
  (first, second) => second.length - first.length
);
const placeUnits = new Set(['county', 'parish']);
const stateUnitWords = new Set(['county', 'parish', 'co']);
const keywordStopWords = new Set([
  'a',
  'an',
  'and',
  'arrest',
  'arrests',
  'background',
  'by',
  'check',
  'checks',
  'court',
  'courts',
  'criminal',
  'find',
  'for',
  'free',
  'how',
  'in',
  'inmate',
  'jail',
  'lookup',
  'me',
  'near',
  'of',
  'online',
  'people',
  'police',
  'public',
  'record',
  'records',
  'search',
  'the',
  'to',
  'warrant',
  'warrants'
]);

/**
 * Normalizes a `geo`, `st` or `state` param, rejecting anything outside letters, spaces and apostrophes
 *
 * @param {*} value - route query value
 * @returns {string} lowercase words separated by single spaces, empty when invalid
 */
const normalizePlaceParam = value => {
  if (typeof value !== 'string') {
    return '';
  }

  const text = value
    .toLowerCase()
    .replace(/[+_-]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

  return /^[a-z' ]+$/.test(text) ? text : '';
};

/**
 * Gets a state place from a lowercase state name
 *
 * @param {string} name - e.g. `new york`
 * @returns {{name: string, state: string}|null}
 */
const getStatePlace = name => {
  const code = stateCodesByName[name];

  if (!code) {
    return null;
  }

  return { name: statesByCode[code], state: code };
};

/**
 * Parses the Google Ads `geo` param, a slug of a state, county, parish, borough or city
 *
 * @param {*} geo - e.g. `texas`, `harris-county`, `st-louis`
 * @returns {{name: string, state: string|null}|null}
 */
const parseGeo = geo => {
  const text = normalizePlaceParam(geo);

  if (!text) {
    return null;
  }

  if (washingtonDcNames.has(text)) {
    return washingtonDc;
  }

  return (
    getStatePlace(text) || { name: formatPlaceName({ text }), state: null }
  );
};

/**
 * Parses an `st` or `state` param, a state code or name
 *
 * @param {*} state - e.g. `TX`, `texas`
 * @returns {{name: string, state: string|null}|null}
 */
const parseStateParam = state => {
  const text = normalizePlaceParam(state);

  if (washingtonDcNames.has(text)) {
    return washingtonDc;
  }

  const code = text.toUpperCase();

  if (text.length === 2 && statesByCode[code]) {
    return { name: statesByCode[code], state: code };
  }

  return getStatePlace(text);
};

/**
 * Gets a state place from Cloudflare geolocation, skipping non-US visitors whose region codes can collide with US ones
 *
 * @param {object} [location] - e.g. `{ state: 'TX', country: 'US' }` from the `location` store
 * @returns {{name: string, state: string}|null}
 */
const parseLocation = location => {
  if (!location || location.country !== 'US') {
    return null;
  }

  return parseStateParam(location.state);
};

/**
 * Finds a county, parish or state in search keyword text, only ever returning a rebuilt place and never the raw text
 *
 * @param {*} keyword - e.g. `Harris County TX Public Records`
 * @returns {{name: string, state: string|null}|null}
 */
const parseKeyword = keyword => {
  if (typeof keyword !== 'string') {
    return null;
  }

  const text = keyword
    .slice(0, maxKeywordLength)
    .toLowerCase()
    .replace(/[^a-z']+/g, ' ')
    .trim();

  if (/\b(washington dc|district of columbia)\b/.test(text)) {
    return washingtonDc;
  }

  const words = text.split(' ');
  const unitIndex = words.findIndex(word => placeUnits.has(word));
  const nameWords = [];

  // Up to 3 words before `county` or `parish`, stopping at search terms like `public records`
  for (let index = unitIndex - 1; index >= 0 && nameWords.length < 3; index--) {
    if (keywordStopWords.has(words[index])) {
      break;
    }

    nameWords.unshift(words[index]);
  }

  if (nameWords.length) {
    const name = [...nameWords, words[unitIndex]].join(' ');

    return { name: formatPlaceName({ text: name }), state: null };
  }

  // A state name not followed by a county unit, and never `kansas city`
  const stateName = stateNames.find(name => {
    const match = new RegExp(`(?:^| )${name}(?:$| ([a-z']+))`).exec(text);

    if (!match) {
      return false;
    }

    const nextWord = match[1];

    return (
      !stateUnitWords.has(nextWord) &&
      !(name === 'kansas' && nextWord === 'city')
    );
  });

  return stateName ? getStatePlace(stateName) : null;
};

/**
 * Resolves the visitor's searched place for landing page copy from Google Ads URL params
 * Order is `geo`, then `st` or `state`, then `keyword`, `kw` or `utm_term` when no `geo` is sent, then the Cloudflare state when an `aid` is sent
 *
 * @param {object} opts
 * @param {object} [opts.query={}] - route query, e.g. `this.$route.query`
 * @param {object} [opts.location] - Cloudflare geolocation, e.g. `{ state: 'TX', country: 'US' }`
 * @returns {{name: string, state: string|null}|null} `state` is a 2-letter state code (`DC` for Washington, DC) or null for a county or city, and the result is null when no place resolves or it is longer than 40 characters
 * @example getLandingPlace({ query: { geo: 'harris-county', st: 'TX' } }) // { name: 'Harris County', state: 'TX' }
 */
const getLandingPlace = (opts = {}) => {
  const query = opts.query || {};
  const statePlace = parseStateParam(query.st || query.state);
  const keywordPlace = query.geo
    ? null
    : parseKeyword(query.keyword || query.kw || query.utm_term);
  const locationPlace = query.aid ? parseLocation(opts.location) : null;
  const place =
    parseGeo(query.geo) || statePlace || keywordPlace || locationPlace;

  if (!place || place.name.length > maxPlaceLength) {
    return null;
  }

  return {
    name: place.name,
    state: place.state || (statePlace && statePlace.state) || null
  };
};

export default getLandingPlace;
